import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useCallback, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CloudUploadOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import IconTextButton from "../buttons/IconTextButton";
import { compressImage } from "utils/index";

interface Image {
  file?: Blob | MediaSource | ArrayBuffer;
  preview: string;
  fileName?: string;
  mimetype?: string;
  id?: string; // Add unique identifier
}

interface ImageUploaderProps {
  label?: string;
  images: Image[] | Image | null;
  setImages: any;
  maxFileSize?: number; // in bytes
  maxFiles?: number;
  sequence?: boolean;
  handleUpload: () => void;
  disabled?: boolean;
  allowedAspectRatios?: number[];
  allowedRange?: number[];
  readOnly?: boolean;
  noUpload?: boolean;
  renamedFileName?: string;
  appendFileName?: string;
  compress?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  label = "Upload trip images *",
  images,
  setImages,
  maxFileSize = 10 * 1024 * 1024, // 5MB
  maxFiles = 10,
  sequence = false,
  handleUpload,
  disabled = false,
  allowedAspectRatios = [16 / 9, 16 / 10],
  allowedRange,
  readOnly = false,
  noUpload = false,
  renamedFileName,
  appendFileName,
  compress = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [isCompressing, setIsCompressing] = useState(false);

  const revokeObjectURLs = (imagesToCleanup: Image[] | Image | null) => {
    if (!imagesToCleanup) return;

    const imageArray = Array.isArray(imagesToCleanup)
      ? imagesToCleanup
      : [imagesToCleanup];
    imageArray.forEach((image) => {
      if (image.preview && image.preview.startsWith("blob:")) {
        URL.revokeObjectURL(image.preview.split("#")[0]);
      }
    });
  };

  useEffect(() => {
    return () => {
      revokeObjectURLs(images);
    };
  }, []);

  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (allowedRange) {
          const { width, height } = img;
          const aspectRatio = width / height;
          const minRatio = allowedRange[0];
          const maxRatio = allowedRange[1];

          const isValidAspectRatio =
            aspectRatio >= minRatio && aspectRatio <= maxRatio;

          resolve(isValidAspectRatio);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl); // Clean up on error too
        resolve(false);
      };
    });
  };

  const validateFileType = (file: File): boolean => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        `File ${file.name} is not supported. Only PNG and JPG images are allowed.`
      );
      return false;
    }
    return true;
  };

  const generateUniqueId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const createPreviewUrl = (file: File): string => {
    const objectUrl = URL.createObjectURL(file);
    return `${objectUrl}#${Date.now()}-${Math.random()}`;
  };

  const handleImageUpload = useCallback(
    async (files: FileList) => {
      let currentImages = Array.isArray(images)
        ? images
        : images
        ? [images]
        : [];

      if (currentImages.length >= maxFiles) {
        toast.error(`You can only upload up to ${maxFiles} images.`);
        return;
      }

      setIsCompressing(true);

      try {
        const validImages = await Promise.all(
          Array.from(files).map(async (file, index) => {
            if (!validateFileType(file)) {
              return null;
            }

            if (file.size > maxFileSize) {
              toast.error(
                `Your File ${file.name.substring(0, 10)}... exceeds the maximum file size limit.`
              );
              return null;
            }

            const isValidDimensions = await validateImageDimensions(file);
            if (!isValidDimensions) {
              toast.error(
                `File ${file.name} dimensions do not match the allowed aspect ratios.`
              );
              return null;
            }

            let fileName = renamedFileName ?? file.name;


            const compressData =  compress ? await compressImage(file) : file;
            const data = await compressData.arrayBuffer();

            if (sequence) {
              const sequenceNumber = (currentImages.length + index + 1)
                .toString()
                .padStart(3, "0");
              fileName = appendFileName
                ? `${appendFileName}-${sequenceNumber}`
                : sequenceNumber;
            }

            return {
              id: generateUniqueId(),
              file: data,
              preview: createPreviewUrl(file),
              fileName,
              mimetype: compressData.type,
            };
          })
        );

        const filteredImages = validImages.filter(
          (image) => image !== null
        ) as Image[];

        if (maxFiles === 1) {
          if (images) {
            revokeObjectURLs(images);
          }
          setImages(filteredImages[0] || null);
        } else {
          const totalImages = [...currentImages, ...filteredImages];
          totalImages.forEach((img, i) => {
            const sequenceNumber = (i + 1).toString().padStart(3, "0");
            img.fileName = appendFileName
              ? `${appendFileName}-${sequenceNumber}`
              : sequenceNumber;
          });
          setImages(totalImages.slice(0, maxFiles));
        }
      } catch (error) {
        toast.error("Error processing images. Please try again.");
        console.error("Image compression error:", error);
      } finally {
        setIsCompressing(false);
      }
    },
    [
      maxFileSize,
      setImages,
      sequence,
      images,
      maxFiles,
      allowedRange,
      renamedFileName,
      appendFileName,
    ]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      handleImageUpload(event.target.files);
      event.target.value = "";
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled || isCompressing) return;
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleImageUpload(event.dataTransfer.files);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled || isCompressing) return;
    event.preventDefault();
  };

  const handleRemoveImage = () => {
    if (images) {
      revokeObjectURLs(images);
    }
    setImages(null);
  };

  const handleRemoveImageFromList = (index: number) => {
    if (Array.isArray(images)) {
      const imageToRemove = images[index];
      if (imageToRemove) {
        revokeObjectURLs(imageToRemove);
      }

      let updatedImages = images
        .filter((_, i) => i !== index)
        .map((img, i) => {
          const sequenceNumber = (i + 1).toString().padStart(3, "0");
          return {
            ...img,
            fileName: appendFileName
              ? `${appendFileName}-${sequenceNumber}`
              : sequenceNumber,
          };
        });

      setImages(updatedImages);
    } else {
      handleRemoveImage();
    }
  };

  const formatAspectRatio = (aspectRatio: number): string => {
    const predefinedRatios: { [key: number]: string } = {
      [16 / 9]: "16:9",
      [9 / 16]: "9:16",
      [16 / 10]: "16:10",
    };

    return predefinedRatios[aspectRatio] || aspectRatio.toFixed(2); // Fallback if not in predefined list
  };

  // Determine the effective disabled state (either explicitly disabled or in loading/compressing state)
  const isDisabled = disabled || isCompressing;

  return (
    <Box mt={3}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "center" : "space-between"}
        alignItems={isMobile ? "center" : "flex-start"}
        flexWrap="wrap"
        mr={isMobile ? 0 : 6}
        gap={isMobile ? 2 : 0}
      >
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: "2px solid #E9EAEB",
            borderRadius: 4,
            padding: 3,
            textAlign: "center",
            cursor: isDisabled ? "default" : "pointer",
            width: isMobile ? "100%" : isTablet ? "80%" : "36%",
            minWidth: isMobile ? "100%" : 350,
            maxWidth: isMobile ? "100%" : "none",
            display: readOnly ? "none" : "",
            opacity: isDisabled ? 0.7 : 1,
            position: "relative",
          }}
        >
          {isCompressing && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                borderRadius: 4,
                zIndex: 1,
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <CircularProgress size={40} />
              </Box>
            </Box>
          )}
          <Button
            variant="outlined"
            component="label"
            disabled={isDisabled}
            sx={{
              py: 1,
              px: 0,
              margin: 0,
              minWidth: 50,
              borderRadius: 2,
              borderColor: "transparent",
              boxShadow: "4px 2px 6px 2px rgba(0, 0, 0, 0.25)",
              "&:hover": {
                transform: isDisabled ? "none" : "scale(1.03)",
              },
            }}
          >
            <CloudUploadOutlined sx={{ padding: 0, margin: 0 }} />
            <input
              type="file"
              hidden
              accept="image/png,image/jpeg,image/jpg"
              multiple
              onChange={handleFileChange}
              disabled={isDisabled}
              key={Date.now()} // Force re-render of input to ensure fresh state
            />
          </Button>
          <Typography
            variant="body1"
            color="#3F3F46"
            mt={2}
            fontFamily={"Inter"}
            fontWeight={600}
            fontSize={14}
          >
            Click to Add images or drag and drop
          </Typography>
          <Typography
            variant="body1"
            color="#535862"
            mt={1}
            fontFamily={"Inter"}
            fontWeight={400}
            fontSize={12}
          >
            PNG, JPG ( Allowed Aspect Ratios:{" "}
            {allowedAspectRatios.map(formatAspectRatio).join(", ")})
          </Typography>
        </Box>
        <Box position="relative" sx={{ width: isMobile ? "100%" : "auto" }}>
          <IconTextButton
            onClick={handleUpload}
            label={label}
            disabled={isCompressing || isDisabled}
            variant="text"
            sx={{
              backgroundColor: "#000000",
              display: readOnly || noUpload ? "none" : "",
              width: isMobile ? "100%" : "auto",
              mt: isMobile ? 2 : 0,
              opacity: isCompressing ? 0.8 : 1,
            }}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        gap={2}
        mt={2}
        flexWrap="wrap"
        justifyContent={isMobile ? "center" : "flex-start"}
      >
        {Array.isArray(images)
          ? images.map((image, index) => (
              <ImagePreview
                key={image.id || `${index}-${image.fileName}`}
                image={image}
                onRemove={() => handleRemoveImageFromList(index)}
                disabled={isDisabled}
              />
            ))
          : images !== null && (
              <ImagePreview
                key={images.id || images.fileName}
                image={images}
                onRemove={handleRemoveImage}
                disabled={isDisabled}
              />
            )}
      </Box>
    </Box>
  );
};

const ImagePreview: React.FC<{
  image: Image;
  onRemove: () => void;
  disabled: boolean;
}> = ({ image, onRemove, disabled }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box position="relative">
      <img
        src={image.preview}
        alt="Trip"
        style={{
          width: isMobile ? 80 : 100,
          height: isMobile ? 80 : 100,
          borderRadius: 10,
          objectFit: "cover",
        }}
        loading="lazy"
        key={image.preview}
      />
      <IconButton
        size="small"
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: "white",
          padding: isMobile ? "2px" : "4px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
        disabled={disabled}
        onClick={onRemove}
      >
        <CloseIcon fontSize={isMobile ? "small" : "small"} />
      </IconButton>
    </Box>
  );
};

export default ImageUploader;
